import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ResponsiveHeatMapCanvas } from "@nivo/heatmap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import ErrorScreen from "../../../../components/error/ErrorScreen";
import { CloseIcon } from "../../../Common/SvgIcons/WorkflowIcons";
import NivoTextParser from "./NivoTextParser";
import nivoTheme from "./nivoTheme";
import Prediction from "../../../../components/modal/NavPanes/Prediction";

const MAX_AXIS_TICKS = 40;
const MIN_CELL_SIZE_FOR_LABEL = 9;
const MAX_LABEL_CELLS = 2500;

const estimateLabelWidth = (label, fontSize = 12) =>
  Math.ceil(String(label ?? "").length * fontSize * 0.62);

const getCssVariable = (name, fallback) => {
  if (typeof window === "undefined") return fallback;

  return (
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
    fallback
  );
};

const getHeatMapTheme = (_themeKey) => {
  const textColor = getCssVariable("--text-color", "#1f2937");
  const gridColor = getCssVariable("--glass-color", "#e5e7eb");

  return {
    ...nivoTheme,
    background: "transparent",
    text: {
      fill: textColor,
      fontSize: 13,
    },
    axis: {
      domain: {
        line: {
          stroke: gridColor,
          strokeWidth: 1,
        },
      },
      ticks: {
        line: {
          stroke: textColor,
        },
        text: {
          fill: textColor,
          fontSize: 13,
          fontWeight: 700,
        },
      },
      legend: {
        text: {
          fill: textColor,
          fontSize: 14,
          fontWeight: 700,
        },
      },
    },
    legends: {
      text: {
        fill: textColor,
        fontSize: 12,
      },
      ticks: {
        line: {
          stroke: textColor,
          strokeWidth: 1,
        },
        text: {
          fill: textColor,
          fontSize: 12,
        },
      },
      title: {
        text: {
          fill: textColor,
          fontSize: 13,
          fontWeight: 600,
        },
      },
    },
    labels: {
      text: {
        fill: textColor,
        fontSize: 12,
      },
    },
  };
};

const getSparseTicks = (items) => {
  if (!Array.isArray(items) || items.length <= MAX_AXIS_TICKS) return items;
  const step = Math.ceil(items.length / MAX_AXIS_TICKS);
  return items.filter((_, index) => index % step === 0);
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "number") return value;

  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return Number.isInteger(value) ? value : value.toFixed(2);
};

const getCellLabelColor = (value, valueExtent) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || valueExtent.max === valueExtent.min) {
    return "var(--text-color)";
  }

  const ratio =
    (numericValue - valueExtent.min) / (valueExtent.max - valueExtent.min);

  return ratio > 0.58 ? "#f8fafc" : "#1f2937";
};

const getHeatMapPlotValue = (cell) =>
  cell?.rawCell?.plot_value ??
  cell?.rawCell?.plotValue ??
  cell?.rawCell?.indexValue ??
  cell?.rawCell?.category ??
  cell?.rawCell?.label ??
  cell?.predicted ??
  cell?.actual;

function HeatMapCanvasView({
  safeData,
  margin,
  chartTheme,
  sparseXTicks,
  sparseYTicks,
  xLabel,
  yLabel,
  valueLegendTitle,
  valueExtent,
  xTickRotation,
  valueFormat,
  onCellClick,
}) {
  const [hoveredCell, setHoveredCell] = useState(null);
  const [chartBox, setChartBox] = useState(null);
  const [chartElement, setChartElement] = useState(null);
  const cellBorderColor = getCssVariable("--border-gray", "#94a3b8");
  const legendLength = Math.min(
    480,
    Math.max(240, (chartBox?.chartWidth || 400) * 0.7)
  );
  const legendTickValues = useMemo(() => {
    const { min, max } = valueExtent;

    if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;
    if (min === max) return [min];

    const tickCount = Math.max(3, Math.min(6, Math.floor(legendLength / 80)));
    return Array.from(
      { length: tickCount },
      (_, index) => min + ((max - min) * index) / (tickCount - 1)
    );
  }, [legendLength, valueExtent]);

  useEffect(() => {
    if (!chartElement) return undefined;

    const updateChartBox = () => {
      const rect = chartElement.getBoundingClientRect();
      const chartWidth = rect.width - margin.left - margin.right;
      const chartHeight = rect.height - margin.top - margin.bottom;

      setChartBox({
        width: rect.width,
        height: rect.height,
        chartWidth,
        chartHeight,
      });
    };

    updateChartBox();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateChartBox)
        : null;

    resizeObserver?.observe(chartElement);
    window.addEventListener("resize", updateChartBox);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateChartBox);
    };
  }, [chartElement, margin]);

  const getCellFromEvent = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();

    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const chartWidth = rect.width - margin.left - margin.right;
    const chartHeight = rect.height - margin.top - margin.bottom;

    setChartBox({
      width: rect.width,
      height: rect.height,
      chartWidth,
      chartHeight,
    });

    const innerX = mouseX - margin.left;
    const innerY = mouseY - margin.top;

    if (
      innerX < 0 ||
      innerY < 0 ||
      innerX > chartWidth ||
      innerY > chartHeight
    ) {
      return null;
    }

    const rowCount = safeData.length;
    const colCount = safeData[0]?.data?.length ?? 0;

    const colIndex = Math.floor((innerX / chartWidth) * colCount);
    const rowIndex = Math.floor((innerY / chartHeight) * rowCount);

    const row = safeData[rowIndex];
    const cell = row?.data?.[colIndex];

    if (!row || !cell) {
      return null;
    }

    return {
      left: mouseX + 12,
      top: mouseY + 12,
      actual: row.id,
      predicted: cell.x,
      value: cell.y,
      rawCell: cell,
      rawRow: row,
    };
  };

  const handleMouseMove = (event) => {
    const nextCell = getCellFromEvent(event);

    if (!nextCell) {
      setHoveredCell(null);
      return;
    }

    setHoveredCell(nextCell);
  };

  const handleClick = (event) => {
    if (!onCellClick) return;

    const clickedCell = getCellFromEvent(event);
    if (!clickedCell) return;

    event.stopPropagation();
    onCellClick(clickedCell);
  };

  const renderCellLabels = () => {
    if (!chartBox) return null;

    const rowCount = safeData.length;
    const colCount = safeData[0]?.data?.length ?? 0;

    if (!rowCount || !colCount) return null;
    if (rowCount * colCount > MAX_LABEL_CELLS) return null;

    const cellWidth = chartBox.chartWidth / colCount;
    const cellHeight = chartBox.chartHeight / rowCount;

    if (
      cellWidth < MIN_CELL_SIZE_FOR_LABEL ||
      cellHeight < MIN_CELL_SIZE_FOR_LABEL
    ) {
      return null;
    }

    return (
      <div
        style={{
          position: "absolute",
          left: margin.left,
          top: margin.top,
          width: chartBox.chartWidth,
          height: chartBox.chartHeight,
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        {safeData.flatMap((row, rowIndex) =>
          (row.data || []).map((cell, colIndex) => {
            const labelColor = getCellLabelColor(cell.y, valueExtent);

            return (
              <div
                key={`${row.id}-${cell.x}`}
                style={{
                  position: "absolute",
                  left: colIndex * cellWidth,
                  top: rowIndex * cellHeight,
                  width: cellWidth,
                  height: cellHeight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: Math.max(
                    10,
                    Math.min(14, cellWidth / 3.5, cellHeight * 0.5)
                  ),
                  fontWeight: 600,
                  lineHeight: 1,
                  color: labelColor,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textShadow:
                    labelColor === "#f8fafc"
                      ? "0 1px 2px rgba(15, 23, 42, 0.72)"
                      : "0 1px 2px rgba(255, 255, 255, 0.55)",
                }}
              >
                {formatValue(cell.y)}
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <div
      ref={setChartElement}
      style={{ height: "100%", position: "relative" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoveredCell(null)}
      onClick={handleClick}
    >
      <ResponsiveHeatMapCanvas
        data={safeData}
        margin={margin}
        valueFormat={valueFormat}
        isInteractive={false}
        enableLabels={false}
        animate={false}
        theme={chartTheme}
        axisTop={{
          tickValues: sparseXTicks,
          tickSize: 5,
          tickPadding: 8,
          tickRotation: xTickRotation,
          legend: xLabel,
          legendPosition: "middle",
          legendOffset: xLabel
            ? xTickRotation === 0
              ? -46
              : xTickRotation === -45
                ? -72
                : -Math.max(72, margin.top - 28)
            : -30,
        }}
        axisLeft={{
          tickValues: sparseYTicks,
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          legend: yLabel,
          legendPosition: "middle",
          legendOffset: yLabel ? -Math.max(56, margin.left - 28) : -40,
        }}
        colors={{
          type: "sequential",
          scheme: "blues",
        }}
        emptyColor="#555555"
        borderWidth={1}
        borderColor={cellBorderColor}
        legends={[
          {
            anchor: "bottom",
            translateX: 0,
            translateY: 36,
            length: legendLength,
            thickness: 10,
            direction: "row",
            ticks: legendTickValues,
            tickPosition: "after",
            tickSize: 4,
            tickSpacing: 6,
            tickOverlap: false,
            tickFormat: formatValue,
            title: valueLegendTitle || "Value →",
            titleAlign: "start",
            titleOffset: 6,
          },
        ]}
      />

      {chartBox && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: margin.left,
            top: margin.top,
            width: chartBox.chartWidth,
            height: chartBox.chartHeight,
            border: `2px solid ${cellBorderColor}`,
            boxSizing: "border-box",
            pointerEvents: "none",
            zIndex: 3,
          }}
        />
      )}

      {renderCellLabels()}

      {hoveredCell && (
        <div
          style={{
            position: "absolute",
            left: hoveredCell.left,
            top: hoveredCell.top,
            background: "var(--bg-color8)",
            border: "1px solid var(--glass-color)",
            borderRadius: 6,
            color: "var(--text-color)",
            padding: "8px 10px",
            fontSize: 13,
            pointerEvents: "none",
            zIndex: 5,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            maxWidth: 320,
          }}
        >
          <div>
            <b>{yLabel || "Row"}:</b> {hoveredCell.actual}
          </div>
          <div>
            <b>{xLabel || "Column"}:</b> {hoveredCell.predicted}
          </div>
          <div>
            <b>{valueLegendTitle || "Value"}:</b> {formatValue(hoveredCell.value)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function HeatMapGraph({
  data,
  title,
  xLabel,
  yLabel,
  valueLegendTitle,
  height,
  evaluation_id,
  modelName,
  task_type,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [predictions, setPredictions] = useState(false);
  const [selectedCellData, setSelectedCellData] = useState(null);

  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const chartHeight = useMemo(() => {
    if (typeof height === "number" && height > 0) return height;
    const rows = safeData.length;
    // Scale so every row label stays readable when many rows are plotted.
    return Math.max(420, Math.min(960, 140 + rows * 34));
  }, [height, safeData.length]);
  const themeKey =
    typeof document === "undefined"
      ? "light"
      : document.documentElement.getAttribute("data-theme") || "light";
  const chartTheme = useMemo(() => getHeatMapTheme(themeKey), [themeKey]);

  const xLabels = useMemo(
    () => safeData?.[0]?.data?.map((item) => item.x) ?? [],
    [safeData]
  );

  const yLabels = useMemo(() => safeData.map((item) => item.id), [safeData]);

  const xTickRotation = useMemo(() => {
    const maxLen = xLabels.reduce(
      (max, label) => Math.max(max, String(label ?? "").length),
      0
    );
    if (xLabels.length <= 6 && maxLen <= 8) return 0;
    if (xLabels.length <= 12 && maxLen <= 12) return -45;
    return -90;
  }, [xLabels]);

  const margin = useMemo(() => {
    const maxYWidth = yLabels.reduce(
      (max, label) => Math.max(max, estimateLabelWidth(label, 13)),
      0
    );
    const maxXWidth = xLabels.reduce(
      (max, label) => Math.max(max, estimateLabelWidth(label, 13)),
      0
    );
    const left = Math.min(
      300,
      Math.max(yLabel ? 150 : 112, maxYWidth + (yLabel ? 48 : 28))
    );
    const topBase =
      xTickRotation === 0 ? 56 : xTickRotation === -45 ? 88 : Math.max(100, maxXWidth + 24);
    const top = topBase + (xLabel ? 28 : 8);
    return {
      top,
      right: 28,
      bottom: 78,
      left,
    };
  }, [xLabel, yLabel, xLabels, yLabels, xTickRotation]);

  const sparseXTicks = useMemo(() => getSparseTicks(xLabels), [xLabels]);
  const sparseYTicks = useMemo(() => getSparseTicks(yLabels), [yLabels]);

  const valueExtent = useMemo(() => {
    const values = safeData
      .flatMap((row) => row?.data?.map((cell) => Number(cell?.y)) ?? [])
      .filter(Number.isFinite);

    if (!values.length) return { min: 0, max: 0 };

    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, [safeData]);

  const valueFormat = useMemo(() => {
    const values = safeData
      .flatMap((row) => row?.data?.map((cell) => Number(cell?.y)) ?? [])
      .filter(Number.isFinite);
    if (values.length && values.every((value) => Number.isInteger(value))) {
      return ">-.0f";
    }
    return ">-.2f";
  }, [safeData]);

  const handleCellClick = useCallback((cell) => {
    if (!isModalOpen) return;
    if (!evaluation_id) return;

    const plotValue = getHeatMapPlotValue(cell);
    if (plotValue === null || plotValue === undefined || plotValue === "") return;

    setSelectedCellData({
      ...cell,
      chartTitle: title,
      indexValue: plotValue,
    });
    setPredictions(true);
  }, [evaluation_id, isModalOpen, title]);

  if (!safeData.length) {
    return <ErrorScreen message="No Heatmap found" />;
  }

  const renderChart = (options = {}) => (
    <HeatMapCanvasView
      safeData={safeData}
      margin={margin}
      chartTheme={chartTheme}
      sparseXTicks={sparseXTicks}
      sparseYTicks={sparseYTicks}
      xLabel={xLabel}
      yLabel={yLabel}
      valueLegendTitle={valueLegendTitle}
      valueExtent={valueExtent}
      xTickRotation={xTickRotation}
      valueFormat={valueFormat}
      onCellClick={options.onCellClick}
    />
  );

  return (
    <>
      <div
        className="chart badge-border p-4"
        style={{ height: chartHeight, position: "relative" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          if (!isModalOpen) setIsModalOpen(true);
        }}
      >
        <NivoTextParser title={title} />

        {isHovered && (
          <div
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              cursor: "pointer",
              zIndex: 3,
            }}
            onClick={(event) => {
              event.stopPropagation();
              setIsModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faExpand} title="Expand" />
          </div>
        )}

        <div style={{ height: "calc(100% - 52px)", position: "relative" }}>
          {renderChart()}
        </div>
      </div>

      {isModalOpen && createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1055,
          }}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="pt-0 pe-0 container-card w-75 w-md-100"
            style={{
              backgroundColor: "var(--bg-color8)",
              padding: "50px",
              height: "70%",
              position: "relative",
              overflow: "visible",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close expanded chart"
              className="plain-button text-danger"
              onClick={(event) => {
                event.stopPropagation();
                setIsModalOpen(false);
              }}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                cursor: "pointer",
                zIndex: 4,
              }}
            >
              <CloseIcon className="wf-icon text-secondary" />
            </button>

            <div className="p-3" style={{ width: "100%", height: "100%" }}>
              <div style={{ height: "50px" }}>
                <NivoTextParser title={title} />
              </div>

              <div style={{ height: "calc(100% - 50px)", position: "relative" }}>
                {renderChart({ onCellClick: handleCellClick })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {predictions && (
        <div
          className="sample-bg mt-0 mt-md-4"
          style={{
            position: "fixed",
            top: "1",
          }}
          onClick={() => setPredictions(false)}
        >
          <div
            className="modal-905 w-100 mx-0 mb-0"
            onClick={(event) => event.stopPropagation()}
          >
            <Prediction
              type="bars"
              sliderIndex={sliderIndex}
              setSliderIndex={setSliderIndex}
              setPredictions={setPredictions}
              barData={selectedCellData}
              bar_url="evaluations"
              id={evaluation_id}
              modelName={modelName}
              tasktype={task_type}
            />
          </div>
        </div>
      )}
    </>
  );
}
